import type { RuleGroupType } from 'react-querybuilder';

import type { DatastoreQueryJson } from '../../../shared/types/types';

interface RuleGroupLike {
  rules?: unknown[];
  combinator?: string;
}

function isRuleGroup(item: unknown): item is RuleGroupLike {
  return (
    item !== null &&
    typeof item === 'object' &&
    'rules' in item &&
    Array.isArray((item as RuleGroupLike).rules)
  );
}

/** Count all rules (non-group items) in a query tree. */
export function countTotalRules(group: RuleGroupType): number {
  let count = 0;

  group.rules.forEach((item) => {
    if (isRuleGroup(item)) {
      count += countTotalRules(item as unknown as RuleGroupType);
    } else {
      count += 1;
    }
  });

  return count;
}

/** Count direct rules (non-group) in a single group. */
export function countRulesInGroup(group: RuleGroupType): number {
  return group.rules.filter((item) => !isRuleGroup(item)).length;
}

/** Count direct sub-groups in a single group. */
export function countGroupsInGroup(group: RuleGroupType): number {
  return group.rules.filter((item) => isRuleGroup(item)).length;
}

/** Get the maximum nesting depth of a query tree (root = 1). */
export function getMaxDepth(group: RuleGroupType, currentDepth = 1): number {
  let maxDepth = currentDepth;

  group.rules.forEach((item) => {
    if (isRuleGroup(item)) {
      const childDepth = getMaxDepth(
        item as unknown as RuleGroupType,
        currentDepth + 1
      );

      if (childDepth > maxDepth) {
        maxDepth = childDepth;
      }
    }
  });

  return maxDepth;
}

/** Navigate to the group at the given path in the query tree. Path [] = root. */
function getGroupAtPath(
  root: RuleGroupType,
  path: number[]
): RuleGroupType | null {
  let current: RuleGroupType = root;

  for (let i = 0; i < path.length; i += 1) {
    const idx = path[i];
    const child = current.rules[idx];

    if (!isRuleGroup(child)) {
      return null;
    }

    current = child as unknown as RuleGroupType;
  }

  return current;
}

export interface QueryLimitStatus {
  /** Whether adding a rule is blocked at the given path. */
  canAddRule: boolean;
  /** Whether adding a group is blocked at the given path. */
  canAddGroup: boolean;
  /** Reason why adding a rule is blocked (empty if allowed). */
  addRuleBlockReason: string;
  /** Reason why adding a group is blocked (empty if allowed). */
  addGroupBlockReason: string;
}

export interface QueryLimitOverrides {
  maxRulesPerGroup?: number;
  maxNestedGroupDepth?: number;
  maxTotalRules?: number;
  maxTotalGroups?: number;
}

export const DEFAULT_QUERY_FILTER_LIMITS: Required<QueryLimitOverrides> = {
  maxRulesPerGroup: 10,
  maxNestedGroupDepth: 1,
  maxTotalRules: 10,
  maxTotalGroups: 3,
};

/**
 * Check whether a rule or group can be added at a given path.
 * Used by the query builder to disable add buttons.
 * Pass `limits` to override the built-in constants (e.g. from the config API).
 */
export function checkQueryLimits(
  query: RuleGroupType,
  path: number[],
  limits?: QueryLimitOverrides
): QueryLimitStatus {
  const maxRulesPerGroup =
    limits?.maxRulesPerGroup ?? DEFAULT_QUERY_FILTER_LIMITS.maxRulesPerGroup;
  const maxNestedGroupDepth =
    limits?.maxNestedGroupDepth ??
    DEFAULT_QUERY_FILTER_LIMITS.maxNestedGroupDepth;
  const maxTotalRules =
    limits?.maxTotalRules ?? DEFAULT_QUERY_FILTER_LIMITS.maxTotalRules;
  const maxTotalGroups =
    limits?.maxTotalGroups ?? DEFAULT_QUERY_FILTER_LIMITS.maxTotalGroups;

  const result: QueryLimitStatus = {
    canAddRule: true,
    canAddGroup: true,
    addRuleBlockReason: '',
    addGroupBlockReason: '',
  };

  const totalRules = countTotalRules(query);

  if (totalRules >= maxTotalRules) {
    result.canAddRule = false;
    result.addRuleBlockReason = `Maximum of ${maxTotalRules} total rules reached`;
  }

  const group = getGroupAtPath(query, path);

  if (group) {
    const rulesInGroup = countRulesInGroup(group);

    if (rulesInGroup >= maxRulesPerGroup && result.canAddRule) {
      result.canAddRule = false;
      result.addRuleBlockReason = `Maximum of ${maxRulesPerGroup} rules per group reached`;
    }

    const groupsInGroup = countGroupsInGroup(group);

    if (groupsInGroup >= maxTotalGroups) {
      result.canAddGroup = false;
      result.addGroupBlockReason = `Maximum of ${maxTotalGroups} groups reached`;
    }
  }

  if (path.length >= maxNestedGroupDepth) {
    result.canAddGroup = false;
    result.addGroupBlockReason = `Maximum nesting depth of ${maxNestedGroupDepth} levels reached`;
  }

  return result;
}

/**
 * Operators that are unary — they don't require a value input
 * (e.g., "is null", "is not null", "is empty", "is not empty").
 */
export const UNARY_OPERATORS = new Set([
  'null',
  'notNull',
  'empty',
  'notEmpty',
]);

/** Operators that require array values (set-based). */
const LIST_OPERATORS = new Set(['in', 'notIn']);

/** Operators that require two-element array values (range-based). */
const BETWEEN_OPERATORS = new Set(['between', 'notBetween']);

/**
 * Recursively walks a react-querybuilder RuleGroupType tree and normalizes
 * rule values based on operator type:
 * - Unary operators: sets value to null
 * - List operators (in/notIn): ensures value is an array
 * - Scalar operators: ensures value is a string (not an array)
 * Returns a new tree (does not mutate the input).
 */
export function normalizeRuleValues(group: RuleGroupType): RuleGroupType {
  return {
    ...group,
    rules: group.rules.map((item) => {
      if (item && typeof item === 'object' && 'rules' in item) {
        return normalizeRuleValues(item);
      }

      // After the group check above, this branch only handles leaf rules (RuleType).
      const rule = item;

      if (UNARY_OPERATORS.has(rule.operator)) {
        return { ...rule, value: null };
      }

      if (LIST_OPERATORS.has(rule.operator)) {
        // Ensure value is an array for in/notIn operators
        if (Array.isArray(rule.value)) {
          return rule;
        }

        // Convert scalar string to array (split on comma for multi-value)
        // Treat null/undefined as empty (e.g., after switching from a unary operator)
        if (rule.value === null || rule.value === undefined) {
          return { ...rule, value: [] };
        }

        const str = String(rule.value);
        const parts = str
          ? str
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean)
          : [];

        return { ...rule, value: parts };
      }

      if (BETWEEN_OPERATORS.has(rule.operator)) {
        // Between expects a two-element array — leave as-is if already correct
        if (Array.isArray(rule.value)) {
          return rule;
        }

        // Treat null/undefined as empty (e.g., after switching from a unary operator)
        const first =
          rule.value === null || rule.value === undefined
            ? ''
            : String(rule.value);

        return { ...rule, value: [first, ''] };
      }

      // Scalar operators: flatten array to string if needed
      if (Array.isArray(rule.value)) {
        return { ...rule, value: String(rule.value[0] ?? '') };
      }

      // Treat null as empty string for scalar operators (e.g., after switching from unary)
      if (rule.value === null || rule.value === undefined) {
        return { ...rule, value: '' };
      }

      return rule;
    }),
  };
}

/**
 * @deprecated Use {@link normalizeRuleValues} instead.
 * Kept as an alias for backward compatibility.
 */
export const nullifyUnaryOperatorValues = normalizeRuleValues;

/**
 * Type guard that checks if a DatastoreQueryJson object has a `rules` array.
 * DatastoreQueryJson is typed as Record<string, unknown> so we need runtime
 * narrowing to safely access the rule tree structure.
 */
function hasRulesArray(
  obj: Record<string, unknown>
): obj is Record<string, unknown> & { rules: unknown[] } {
  return Array.isArray(obj.rules);
}

/**
 * Variant that operates on the loosely-typed DatastoreQueryJson shape
 * used in the serialization layer (wire utilities).
 *
 * DatastoreQueryJson doesn't expose a `rules` array at the type level
 * (it's typed as Record<string, unknown>), so this function uses runtime
 * narrowing via hasRulesArray rather than compile-time type assertions.
 */
export function normalizeRuleValuesJson(
  query: DatastoreQueryJson
): DatastoreQueryJson {
  const obj = query as Record<string, unknown>;

  if (!hasRulesArray(obj)) {
    return query;
  }

  return {
    ...obj,
    rules: obj.rules.map((item: unknown) => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const rule = item as Record<string, unknown>;

      // Nested group — recurse
      if (Array.isArray(rule.rules)) {
        return normalizeRuleValuesJson(rule as DatastoreQueryJson);
      }

      const operator = String(rule.operator ?? '');

      // Unary operators — nullify value
      if (UNARY_OPERATORS.has(operator)) {
        return { ...rule, value: null };
      }

      // List operators — ensure value is an array
      if (LIST_OPERATORS.has(operator)) {
        if (Array.isArray(rule.value)) {
          return rule;
        }

        // Treat null/undefined as empty (e.g., after switching from a unary operator)
        if (rule.value === null || rule.value === undefined) {
          return { ...rule, value: [] };
        }

        const str = String(rule.value);
        const parts = str
          ? str
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean)
          : [];

        return { ...rule, value: parts };
      }

      // Between operators — ensure value is a two-element array
      if (BETWEEN_OPERATORS.has(operator)) {
        if (Array.isArray(rule.value)) {
          return rule;
        }

        // Treat null/undefined as empty (e.g., after switching from a unary operator)
        const first =
          rule.value === null || rule.value === undefined
            ? ''
            : String(rule.value);

        return { ...rule, value: [first, ''] };
      }

      // Scalar operators — flatten array to string if needed
      if (Array.isArray(rule.value)) {
        return { ...rule, value: String((rule.value as unknown[])[0] ?? '') };
      }

      // Treat null as empty string for scalar operators (e.g., after switching from unary)
      if (rule.value === null || rule.value === undefined) {
        return { ...rule, value: '' };
      }

      return rule;
    }),
  } as DatastoreQueryJson;
}

/**
 * @deprecated Use {@link normalizeRuleValuesJson} instead.
 * Kept as an alias for backward compatibility.
 */
export const nullifyUnaryOperatorValuesJson = normalizeRuleValuesJson;
